/**
 * Shareable project links — "anyone with this link can join this project".
 *
 * Signed-in visitors who open `/join/{token}` become Contributors. All writes
 * and the join itself go through SECURITY DEFINER RPCs (see
 * `supabase/migrations/20260926000000_project_share_links.sql`); the token is
 * generated server-side and only project owners can read it.
 */
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';

export type ProjectShareLink = Tables<'project_share_links'>;

export type ShareLinkStatus = 'valid' | 'disabled' | 'expired' | 'full' | 'invalid';

export interface ShareLinkPreview {
  status: ShareLinkStatus;
  /** Only set while the link is live. */
  projectName: string | null;
}

/**
 * What to tell a visitor when a link won't admit them. Shared by the sign-up
 * screen (from the preview) and the join page (from the join error), so the
 * two can never disagree.
 */
export const SHARE_LINK_UNAVAILABLE_MESSAGES: Record<Exclude<ShareLinkStatus, 'valid'>, string> = {
  disabled: 'This link has been turned off by the project owner.',
  expired: 'This link has expired. Ask the project owner for a new one.',
  full: 'This link has reached its limit. Ask the project owner to raise it.',
  invalid: "This link isn't valid. It may have been reset by the project owner.",
};

export const SHARE_LINK_JOIN_FAILED_MESSAGE = 'Something went wrong joining this project. Please try again.';

/** Path prefix for share links. Also read by AuthPage to keep the visitor on it. */
export const SHARE_LINK_PATH_PREFIX = '/join/';

export function buildShareLinkUrl(token: string): string {
  return `${window.location.origin}${SHARE_LINK_PATH_PREFIX}${token}`;
}

/** The token in the current URL if it is a share link, otherwise null. */
export function getShareLinkTokenFromPath(pathname: string): string | null {
  if (!pathname.startsWith(SHARE_LINK_PATH_PREFIX)) return null;
  const token = pathname.slice(SHARE_LINK_PATH_PREFIX.length).split('/')[0];
  return token || null;
}

export function isShareLinkExpired(link: Pick<ProjectShareLink, 'expires_at'>): boolean {
  return !!link.expires_at && new Date(link.expires_at).getTime() <= Date.now();
}

export function isShareLinkFull(link: Pick<ProjectShareLink, 'max_joins' | 'join_count'>): boolean {
  return link.max_joins !== null && link.join_count >= link.max_joins;
}

export function isShareLinkLive(link: ProjectShareLink): boolean {
  return link.enabled && !isShareLinkExpired(link) && !isShareLinkFull(link);
}

/** Owner-only: the project's link, or null if none has been created yet. */
export async function getProjectShareLink(projectId: string): Promise<ProjectShareLink | null> {
  const { data, error } = await supabase
    .from('project_share_links')
    .select('*')
    .eq('project_id', projectId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

/** Create the link, or reset it to a new token (the old URL stops working). */
export async function rotateProjectShareLink(projectId: string): Promise<ProjectShareLink> {
  const { data, error } = await supabase.rpc('rotate_project_share_link', { p_project_id: projectId });
  if (error) throw error;
  return data;
}

export async function setProjectShareLinkSettings(
  projectId: string,
  settings: { enabled: boolean; expiresAt: string | null; maxJoins: number | null },
): Promise<ProjectShareLink> {
  const { data, error } = await supabase.rpc('set_project_share_link_settings', {
    p_project_id: projectId,
    p_enabled: settings.enabled,
    p_expires_at: settings.expiresAt,
    p_max_joins: settings.maxJoins,
  });
  if (error) throw error;
  return data;
}

/** Works signed-out. The project name is only returned for a live link. */
export async function getShareLinkPreview(token: string): Promise<ShareLinkPreview> {
  const { data, error } = await supabase.rpc('get_project_share_link_preview', { p_token: token });
  if (error) throw error;
  const row = data?.[0];
  if (!row) return { status: 'invalid', projectName: null };
  return { status: row.status as ShareLinkStatus, projectName: row.project_name };
}

/** Thrown by `joinProjectViaShareLink`; `message` is safe to show the visitor. */
export class ShareLinkJoinError extends Error {}

/**
 * Join the project behind a token. Returns its id. Existing members are left
 * unchanged. The RPC raises `share_link_{status}` for a link that won't admit.
 */
export async function joinProjectViaShareLink(token: string): Promise<string> {
  const { data, error } = await supabase.rpc('join_project_via_share_link', { p_token: token });
  if (error) {
    const status = (Object.keys(SHARE_LINK_UNAVAILABLE_MESSAGES) as (keyof typeof SHARE_LINK_UNAVAILABLE_MESSAGES)[])
      .find((s) => error.message?.includes(`share_link_${s}`));
    throw new ShareLinkJoinError(status ? SHARE_LINK_UNAVAILABLE_MESSAGES[status] : SHARE_LINK_JOIN_FAILED_MESSAGE);
  }
  return data;
}

/**
 * Ask the server to email the project owner(s) that someone joined by link.
 * Safe to call after every join: the Edge Function only sends for a fresh
 * link-join by the caller, and only once. Never throws — a lost notification
 * must not block the join.
 */
export async function notifyOwnersOfShareLinkJoin(projectId: string): Promise<void> {
  try {
    const { error } = await supabase.functions.invoke('notify-share-link-join', { body: { projectId } });
    if (error) console.error('[ShareLink] Owner notification failed:', { projectId, error });
  } catch (err) {
    console.error('[ShareLink] Owner notification failed:', { projectId, err });
  }
}
